/**
 * Input mask helpers for block checkout (A4).
 */

import { __, sprintf } from '@wordpress/i18n';

export function isValidInputMask(mask, maskType) {
    const pattern = String(mask || '').trim();
    if (!pattern) {
        return true;
    }

    if (typeof window.fmeCcfwIsValidInputMask === 'function') {
        return window.fmeCcfwIsValidInputMask(pattern, maskType);
    }

    if (!staticMaskHasValidBrackets(pattern)) {
        return false;
    }

    if (maskType === 'dynamic') {
        if (!/[a9*]\{\d+(?:,\d+)?\}/.test(pattern)) {
            return false;
        }

        const cap = 50;
        const segments = parseDynamicMaskSegments(pattern);
        let hasSlot = false;

        for (let i = 0; i < segments.length; i++) {
            if (segments[i].type !== 'slot') {
                continue;
            }
            hasSlot = true;
            const min = parseInt(segments[i].min, 10);
            const max = parseInt(segments[i].max, 10);
            if (Number.isNaN(min) || Number.isNaN(max) || min < 0 || max < 1 || min > max || max > cap) {
                return false;
            }
        }

        return hasSlot;
    }

    if (/[{}]/.test(pattern)) {
        return false;
    }

    return staticMaskHasSlots(pattern);
}

function staticMaskHasValidBrackets(mask) {
    let depth = 0;

    for (let i = 0; i < mask.length; i++) {
        const ch = mask.charAt(i);

        if (ch === '[') {
            if (depth > 0) {
                return false;
            }
            depth++;
        } else if (ch === ']') {
            if (depth === 0) {
                return false;
            }
            depth--;
        }
    }

    return depth === 0;
}

function parseStaticMaskCharToken(ch) {
    if (ch === '9') {
        return { kind: 'slot', type: 'digit' };
    }
    if (ch === 'a') {
        return { kind: 'slot', type: 'alpha' };
    }
    if (ch === '*') {
        return { kind: 'slot', type: 'alnum' };
    }
    if (/[0-8]/.test(ch)) {
        return { kind: 'slot', type: 'alnum' };
    }
    if (/[A-Za-z]/.test(ch)) {
        return { kind: 'slot', type: 'alnum' };
    }
    return { kind: 'literal', char: ch };
}

function parseStaticMaskTokens(mask) {
    const tokens = [];
    let i = 0;

    while (i < mask.length) {
        if (mask.charAt(i) === '[') {
            const close = mask.indexOf(']', i + 1);
            if (close === -1) {
                tokens.push(parseStaticMaskCharToken(mask.charAt(i)));
                i++;
                continue;
            }

            for (let k = i + 1; k < close; k++) {
                tokens.push({ kind: 'literal', char: mask.charAt(k), fixedGroup: true });
            }

            i = close + 1;
            continue;
        }

        tokens.push(parseStaticMaskCharToken(mask.charAt(i)));
        i++;
    }

    return tokens;
}

function staticMaskHasSlots(mask) {
    return parseStaticMaskTokens(mask).some((token) => token.kind === 'slot');
}

function parseDynamicMaskSegments(mask) {
    const segments = [];
    let i = 0;

    while (i < mask.length) {
        const ch = mask.charAt(i);

        if (ch === '[') {
            const close = mask.indexOf(']', i + 1);
            if (close === -1) {
                segments.push({ type: 'literal', text: '[' });
                i++;
                continue;
            }
            const bracketText = mask.substring(i + 1, close);
            if (bracketText) {
                segments.push({ type: 'literal', text: bracketText, fixedGroup: true });
            }
            i = close + 1;
            continue;
        }

        if (/[a9*]/.test(ch)) {
            const token = ch;
            i++;
            let min = 1;
            let max = 1;

            if (i < mask.length && mask.charAt(i) === '{') {
                const rest = mask.substring(i);
                const qm = rest.match(/^\{(\d+)(?:,(\d+))?\}/);
                if (qm) {
                    min = parseInt(qm[1], 10);
                    max = qm[2] !== undefined ? parseInt(qm[2], 10) : min;
                    i += qm[0].length;
                }
            }

            segments.push({
                type: 'slot',
                token,
                min,
                max,
            });
            continue;
        }

        let lit = '';
        while (i < mask.length) {
            const next = mask.charAt(i);
            if (next === '[' || /[a9*]/.test(next)) {
                break;
            }
            lit += next;
            i++;
        }
        if (lit) {
            segments.push({ type: 'literal', text: lit });
        }
    }

    return segments;
}

function getDynamicMaskSlotChar(token) {
    if (token === '9') {
        return '1';
    }
    if (token === 'a') {
        return 'a';
    }
    return 'x';
}

function buildDynamicMaskDisplayExample(segments) {
    let example = '';

    segments.forEach((segment) => {
        if (segment.type === 'literal') {
            example += segment.text;
            return;
        }

        let count = segment.max;
        if (segment.token === 'a') {
            count = segment.min;
        }

        example += getDynamicMaskSlotChar(segment.token).repeat(Math.max(1, count));
    });

    return example;
}

function buildDynamicMaskRangeDescription(segments, i18n) {
    const parts = [];

    segments.forEach((segment) => {
        if (segment.type === 'literal') {
            if (segment.text === '-') {
                parts.push(i18n.input_mask_literal_hyphen || 'hyphen');
            } else if (segment.text === ' ') {
                parts.push(i18n.input_mask_literal_space || 'space');
            } else {
                const template = i18n.input_mask_literal_separator || 'separator "%s"';
                parts.push(template.replace('%s', segment.text));
            }
            return;
        }

        const min = segment.min;
        const max = segment.max;
        let label;

        if (segment.token === 'a') {
            label = max === 1
                ? (i18n.input_mask_letter_singular || 'letter')
                : (i18n.input_mask_letter_plural || 'letters');
        } else if (segment.token === '9') {
            label = max === 1
                ? (i18n.input_mask_digit_singular || 'digit')
                : (i18n.input_mask_digit_plural || 'digits');
        } else {
            label = i18n.input_mask_alnum_plural || 'alphanumeric characters';
        }

        if (min === max) {
            parts.push(`${min} ${label}`);
        } else {
            parts.push(`${min}-${max} ${label}`);
        }
    });

    return parts.join(', ');
}

function getInputMaskDisplayExample(mask, maskType) {
    const pattern = String(mask || '').trim();
    if (!pattern) {
        return '';
    }

    if (maskType === 'dynamic') {
        return buildDynamicMaskDisplayExample(parseDynamicMaskSegments(pattern));
    }

    let example = '';

    parseStaticMaskTokens(pattern).forEach((token) => {
        if (token.kind === 'literal') {
            example += token.char;
            return;
        }

        if (token.type === 'digit') {
            example += '0';
        } else if (token.type === 'alpha') {
            example += 'A';
        } else {
            example += 'X';
        }
    });

    return example;
}

function getInputMaskSlotCount(mask, maskType) {
    const pattern = String(mask || '').trim();
    if (!pattern || maskType === 'dynamic') {
        return null;
    }

    return parseStaticMaskTokens(pattern).filter((token) => token.kind === 'slot').length;
}

/**
 * Normalize input_mask_settings from PHP / legacy payloads.
 * Handles objects, JSON strings, bare mask patterns, and camelCase keys.
 *
 * @param {*} raw Raw field.input_mask_settings value.
 * @returns {{mask: string, mask_type: string}}
 */
function normalizeInputMaskSettings(raw) {
    let settings = {};

    if (!raw && raw !== 0) {
        return { mask: '', mask_type: 'static' };
    }

    if (typeof raw === 'object' && !Array.isArray(raw)) {
        settings = { ...raw };
    } else if (typeof raw === 'string') {
        const trimmed = String(raw).trim();
        if (!trimmed) {
            return { mask: '', mask_type: 'static' };
        }
        if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
            try {
                const decoded = JSON.parse(trimmed);
                if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
                    settings = decoded;
                }
            } catch (e) {
                // Fall through to bare-pattern handling.
            }
        }
        if (!settings.mask && !settings.pattern && !settings.Mask) {
            // Skip PHP serialize blobs; those must be decoded server-side.
            if (!/^[aOCs]:\d+:/.test(trimmed)) {
                settings = { mask: trimmed, mask_type: 'static' };
            }
        }
    }

    const mask = String(
        settings.mask || settings.pattern || settings.Mask || ''
    ).trim();
    const maskTypeRaw = settings.mask_type || settings.maskType || settings.type || 'static';
    const maskType = maskTypeRaw === 'dynamic' ? 'dynamic' : 'static';

    return {
        mask,
        mask_type: maskType,
    };
}

export function getInputMaskFormatHint(field) {
    if (field.input_mask_format_hint) {
        return field.input_mask_format_hint;
    }

    const settings = normalizeInputMaskSettings(field.input_mask_settings);
    const mask = (settings.mask || '').trim();
    const maskType = settings.mask_type === 'dynamic' ? 'dynamic' : 'static';

    if (!mask || !isValidInputMask(mask, maskType)) {
        return '';
    }

    const i18n = window.fme_upload_data?.i18n || {};

    if (maskType === 'dynamic') {
        const segments = parseDynamicMaskSegments(mask);
        const example = buildDynamicMaskDisplayExample(segments);
        const description = buildDynamicMaskRangeDescription(segments, i18n);
        const template = i18n.input_mask_format_dynamic || 'Format: %1$s (%2$s)';
        return template.replace('%1$s', example).replace('%2$s', description);
    }

    const example = getInputMaskDisplayExample(mask, maskType);
    const slotCount = getInputMaskSlotCount(mask, maskType);

    if (slotCount > 0) {
        const template = i18n.input_mask_format_static || 'Format: %1$s (%2$d characters)';
        return template.replace('%1$s', example).replace('%2$d', String(slotCount)).replace('%2$s', String(slotCount));
    }

    const template = i18n.input_mask_format_simple || 'Format: %s';
    return template.replace('%s', example);
}

export function getInputMaskConfig(field) {
    const settings = normalizeInputMaskSettings(field?.input_mask_settings);
    const mask = (settings.mask || '').trim();
    const maskType = settings.mask_type === 'dynamic' ? 'dynamic' : 'static';

    if (!mask || !isValidInputMask(mask, maskType)) {
        return null;
    }

    return {
        mask,
        maskType,
    };
}

export function hasInputMask(field) {
    return !!getInputMaskConfig(field);
}

function escapeRegexLiteral(char) {
    return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getStaticMaskTokenRegex(ch) {
    if (ch === '9') {
        return '\\d';
    }
    if (ch === 'a') {
        return '[a-zA-Z]';
    }
    if (ch === '*') {
        return '[a-zA-Z0-9]';
    }
    if (/[0-8]/.test(ch)) {
        return '[a-zA-Z0-9]';
    }
    if (/[A-Za-z]/.test(ch)) {
        return '[a-zA-Z0-9]';
    }
    return escapeRegexLiteral(ch);
}

function getStaticMaskSlotRegex(type) {
    if (type === 'digit') {
        return '\\d';
    }
    if (type === 'alpha') {
        return '[a-zA-Z]';
    }
    return '[a-zA-Z0-9]';
}

function buildStaticInputMaskRegex(mask) {
    const pattern = String(mask || '').trim();
    if (!pattern) {
        return null;
    }

    let body = '';

    parseStaticMaskTokens(pattern).forEach((token) => {
        if (token.kind === 'literal') {
            body += escapeRegexLiteral(token.char);
            return;
        }

        body += getStaticMaskSlotRegex(token.type);
    });

    if (!body) {
        return null;
    }

    try {
        return new RegExp(`^${body}$`);
    } catch (error) {
        return null;
    }
}

function buildDynamicInputMaskRegex(mask) {
    const segments = parseDynamicMaskSegments(mask);
    if (!segments.length) {
        return null;
    }

    let body = '';

    segments.forEach((segment) => {
        if (segment.type === 'literal') {
            body += escapeRegexLiteral(segment.text);
            return;
        }

        let slot;
        if (segment.token === '9') {
            slot = '\\d';
        } else if (segment.token === 'a') {
            slot = '[a-zA-Z]';
        } else {
            slot = '[a-zA-Z0-9]';
        }

        if (segment.min === segment.max) {
            body += `${slot}{${segment.min}}`;
        } else {
            body += `${slot}{${segment.min},${segment.max}}`;
        }
    });

    if (!body) {
        return null;
    }

    try {
        return new RegExp(`^${body}$`);
    } catch (error) {
        return null;
    }
}

function buildInputMaskRegex(mask, maskType) {
    if (!mask || !isValidInputMask(mask, maskType)) {
        return null;
    }

    return maskType === 'dynamic'
        ? buildDynamicInputMaskRegex(mask)
        : buildStaticInputMaskRegex(mask);
}

export function isInputMaskValueValid(value, field) {
    if (!hasInputMask(field)) {
        return true;
    }

    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
        return true;
    }

    const config = getInputMaskConfig(field);
    if (!config) {
        return true;
    }

    // Always validate against the mask pattern.
    if (typeof window.fmeCcfwIsInputMaskValueValid === 'function') {
        return window.fmeCcfwIsInputMaskValueValid(trimmed, config);
    }

    const regex = buildInputMaskRegex(config.mask, config.maskType);
    if (!regex) {
        return true;
    }

    return regex.test(trimmed);
}

export function getInputMaskValidationErrorMessage(field) {
    const label = String(field?.field_label || '').trim();
    const hint = String(getInputMaskFormatHint(field) || '')
        .replace(/\s*\|\s*$/, '')
        .trim();
    const base = label
        ? sprintf(
            /* translators: %s: field label */
            __('%s does not match the required format.', 'conditional-checkout-fields-for-woocommerce'),
            label
        )
        : __('This field does not match the required format.', 'conditional-checkout-fields-for-woocommerce');

    if (!hint) {
        return base;
    }

    return `${base} ${hint}`.replace(/\s+/g, ' ').trim();
}

export function applyInputMask(value, field) {
    const config = getInputMaskConfig(field);
    if (!config) {
        return value || '';
    }
    if (typeof window.fmeCcfwApplyInputMask === 'function') {
        return window.fmeCcfwApplyInputMask(value, config);
    }
    return applyInputMaskLocal(value, config);
}

/**
 * Leading [bracket] fixed-group content only (e.g. "+92"), excluding separators
 * that follow the group in the mask (space, "(", "-", etc.).
 */
function getBracketedFixedPrefixCore(tokens) {
    let core = '';

    if (!tokens || !tokens.length) {
        return core;
    }

    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].kind !== 'literal' || !tokens[i].fixedGroup) {
            break;
        }
        core += tokens[i].char;
    }

    return core;
}

function getPrefixLiteralString(tokens) {
    let prefix = '';

    if (!tokens || !tokens.length) {
        return prefix;
    }

    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].kind !== 'literal') {
            break;
        }
        prefix += tokens[i].char;
    }

    return prefix;
}

function skipLeadingSeparatorChars(str) {
    let i = 0;
    while (i < str.length && !/[a-zA-Z0-9]/.test(str.charAt(i))) {
        i += 1;
    }
    return str.substring(i);
}

function getPrefixAlnumCore(core) {
    let alnum = '';
    for (let i = 0; i < core.length; i++) {
        if (/[a-zA-Z0-9]/.test(core.charAt(i))) {
            alnum += core.charAt(i);
        }
    }
    return alnum;
}

/**
 * If value begins with the bracket-prefix core (ignoring [], spaces, and
 * separators; non-alnum core chars like "+" are optional in the paste),
 * return the remainder after that prefix. Otherwise return null.
 * Mirrored from front/assets/js/fme-ccfw-inputmask.js.
 */
function tryStripLeadingBracketPrefix(value, core) {
    const coreAlnum = getPrefixAlnumCore(core);
    if (!coreAlnum) {
        return null;
    }

    let ci = 0;
    let vi = 0;
    while (vi < value.length && ci < coreAlnum.length) {
        const ch = value.charAt(vi);
        if (ch === '[' || ch === ']' || !/[a-zA-Z0-9]/.test(ch)) {
            vi += 1;
            continue;
        }
        if (ch.toLowerCase() === coreAlnum.charAt(ci).toLowerCase()) {
            ci += 1;
            vi += 1;
            continue;
        }
        return null;
    }

    if (ci < coreAlnum.length) {
        return null;
    }

    return value.substring(vi);
}

/**
 * Strip a leading bracketed fixed prefix flexibly so paste like "+92 300...",
 * "[+92] 300...", or "92 300..." does not feed prefix digits into slots.
 * Mirrored from front/assets/js/fme-ccfw-inputmask.js.
 */
function stripFixedPrefixFromValue(str, tokens) {
    const value = String(str || '');

    if (!tokens || !tokens.length) {
        return value;
    }

    const core = getBracketedFixedPrefixCore(tokens);
    if (core) {
        const afterCore = tryStripLeadingBracketPrefix(value, core);
        if (afterCore !== null) {
            return skipLeadingSeparatorChars(afterCore);
        }
    }

    const prefix = getPrefixLiteralString(tokens);
    if (prefix && value.indexOf(prefix) === 0) {
        return value.substring(prefix.length);
    }

    return value;
}

function extractInputChars(value) {
    const chars = [];
    const str = String(value || '');
    for (let i = 0; i < str.length; i++) {
        if (/[a-zA-Z0-9]/.test(str.charAt(i))) {
            chars.push(str.charAt(i));
        }
    }
    return chars;
}

function extractUserInputChars(value, tokens) {
    return extractInputChars(stripFixedPrefixFromValue(String(value || ''), tokens));
}

function charMatchesType(ch, type) {
    if (type === 'digit') {
        return /\d/.test(ch);
    }
    if (type === 'alpha') {
        return /[a-zA-Z]/.test(ch);
    }
    if (type === 'alnum') {
        return /[a-zA-Z0-9]/.test(ch);
    }
    return false;
}

function prefixLiteralCount(tokens) {
    let count = 0;
    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].kind === 'literal') {
            count += 1;
        } else {
            break;
        }
    }
    return count;
}

function slotsInSegmentBefore(tokens, index) {
    let count = 0;
    for (let i = index - 1; i >= 0; i--) {
        if (tokens[i].kind === 'literal') {
            break;
        }
        if (tokens[i].kind === 'slot' && !tokens[i].optional) {
            count += 1;
        }
    }
    return count;
}

function parseDynamicMaskTokens(mask) {
    const tokens = [];
    const segments = parseDynamicMaskSegments(mask);

    segments.forEach((segment) => {
        if (segment.type === 'slot') {
            const slotType = segment.token === '9' ? 'digit' : (segment.token === 'a' ? 'alpha' : 'alnum');
            for (let i = 0; i < segment.max; i++) {
                tokens.push({
                    kind: 'slot',
                    type: slotType,
                    optional: i >= segment.min,
                });
            }
            return;
        }

        for (let j = 0; j < segment.text.length; j++) {
            const lit = { kind: 'literal', char: segment.text.charAt(j) };
            if (segment.fixedGroup) {
                lit.fixedGroup = true;
            }
            tokens.push(lit);
        }
    });

    return tokens;
}

function resolveMaskTokens(config) {
    const mask = String(config.mask || '').trim();
    const maskType = config.maskType || config.mask_type || 'static';

    if (!isValidInputMask(mask, maskType)) {
        return [];
    }

    return maskType === 'dynamic' ? parseDynamicMaskTokens(mask) : parseStaticMaskTokens(mask);
}

function applyMaskSlots(inputChars, tokens) {
    let result = '';
    let vi = 0;
    let segmentSlotCount = 0;
    const prefixLen = prefixLiteralCount(tokens);
    let prefixAdded = false;

    for (let mi = 0; mi < tokens.length; mi++) {
        const token = tokens[mi];

        if (token.kind === 'literal') {
            if (mi < prefixLen) {
                continue;
            }

            const needed = slotsInSegmentBefore(tokens, mi);
            if (needed > 0 && segmentSlotCount >= needed) {
                result += token.char;
                segmentSlotCount = 0;
            }
            continue;
        }

        if (vi >= inputChars.length) {
            if (!token.optional) {
                break;
            }
            continue;
        }

        while (vi < inputChars.length && !charMatchesType(inputChars[vi], token.type)) {
            vi += 1;
        }

        if (vi >= inputChars.length) {
            if (!token.optional) {
                break;
            }
            continue;
        }

        if (prefixLen > 0 && !prefixAdded) {
            for (let pi = 0; pi < prefixLen; pi++) {
                result += tokens[pi].char;
            }
            prefixAdded = true;
        }

        result += inputChars[vi];
        vi += 1;
        segmentSlotCount += 1;

        let lookahead = mi + 1;
        while (lookahead < tokens.length && tokens[lookahead].kind === 'literal') {
            const neededLit = slotsInSegmentBefore(tokens, lookahead);
            if (segmentSlotCount >= neededLit) {
                result += tokens[lookahead].char;
                segmentSlotCount = 0;
                lookahead += 1;
                mi = lookahead - 1;
            } else {
                break;
            }
        }
    }

    return result;
}

function applyInputMaskLocal(value, config) {
    const tokens = resolveMaskTokens(config);
    if (!tokens.length) {
        return value || '';
    }

    const inputChars = extractUserInputChars(String(value || ''), tokens);
    return applyMaskSlots(inputChars, tokens);
}
